import { useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip,
  LinearProgress, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { adminApi } from '../api/client';
import type { Job, JobStatus } from '../api/types';

/** How often to re-fetch a job that hasn't reached a terminal status yet. */
const POLL_MS = 1000;
/** Back off a bit after a failed fetch rather than hammering a down backend. */
const POLL_RETRY_MS = 3000;

const TERMINAL: JobStatus[] = ['succeeded', 'failed', 'cancelled', 'interrupted'];

const STATUS_COLOUR: Record<JobStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  queued: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'warning',
  interrupted: 'warning',
};

interface Props {
  jobId: string;
  onFinished?: (job: Job) => void;
  /** Show the log expanded from the start — useful for long builds. */
  defaultLogOpen?: boolean;
}

/**
 * Live view of one import job.
 *
 * Polls GetJob rather than streaming: the backend's SSE endpoint
 * (`/api/admin/jobs/{id}/stream`) that this used to read from was never
 * ported to the Connect service (see the package doc in
 * backend/internal/jobs/service.go) — it 404s, so an EventSource against it
 * closes immediately and this view sat frozen on its initial state forever,
 * regardless of what the job actually did. GetJob returns the whole row
 * (status/progress/message/log) each time, so a plain poll shows the same
 * thing the stream was meant to and also means opening this after a build
 * has been running for ten minutes shows everything rather than only what
 * happens next. Job state lives in the database precisely so the page can be
 * closed and reopened.
 */
export function JobProgress({ jobId, onFinished, defaultLogOpen = false }: Props) {
  const [status, setStatus] = useState<JobStatus>('queued');
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const logRef = useRef<HTMLPreElement>(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    finishedRef.current = false;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      let job: Job;
      try {
        job = await adminApi.job(jobId);
      } catch {
        if (!cancelled) timer = setTimeout(poll, POLL_RETRY_MS);
        return;
      }
      if (cancelled) return;

      setStatus(job.status);
      setPercent(job.progress);
      setMessage(job.message);
      setLines(job.log ? job.log.split('\n').filter(Boolean) : []);

      if (TERMINAL.includes(job.status)) {
        if (!finishedRef.current) {
          finishedRef.current = true;
          onFinished?.(job);
        }

        return; // reached a terminal status — stop polling
      }

      timer = setTimeout(poll, POLL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // onFinished is intentionally not a dependency: callers commonly pass an
    // inline closure, which would otherwise restart polling every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Keep the newest output in view while the job runs.
  useEffect(() => {
    const element = logRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [lines]);

  const running = status === 'running' || status === 'queued';

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Chip size="small" label={status} color={STATUS_COLOUR[status]} variant="outlined" />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
          {message ?? ''}
        </Typography>
        {running && (
          <Button size="small" color="inherit" onClick={() => adminApi.cancelJob(jobId)}>
            Cancel
          </Button>
        )}
      </Stack>

      <LinearProgress
        variant={running && percent === 0 ? 'indeterminate' : 'determinate'}
        value={percent}
        color={status === 'failed' ? 'error' : status === 'succeeded' ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3 }}
      />

      <Accordion variant="outlined" disableGutters defaultExpanded={defaultLogOpen}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
          <Typography variant="caption">Log ({lines.length} lines)</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box
            component="pre"
            ref={logRef}
            sx={{
              m: 0, p: 1.5, maxHeight: 260, overflow: 'auto',
              fontSize: 11, lineHeight: 1.5, fontFamily: 'ui-monospace, monospace',
              bgcolor: 'action.hover', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}
          >
            {lines.length > 0 ? lines.join('\n') : 'Waiting for output…'}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
